import configurationService from 'common/services/configuration-service/configuration.service';
import { ICommunitySettings, ISystemConfigurationRead } from 'common/services/configuration-service/configuration.service.types';
import { makeAutoObservable } from 'mobx';
import { SYSTEM_CONFIGURATION, COMMUNITY_SETTINGS, NS_SETTINGS } from './configuration.constants';
import React from 'react';
import { FacilityCodeType } from 'common/enums/facility-code-type.enum';
import userAuthStore from '../user-auth/user-auth.store';
import { UserTypeEnum } from 'common/enums/user-type.enum';
import { deactivatedAccessCardName } from 'common/constans/access-card.constants';
import LogoIcon from 'assets/icons/eiq-logo-r.png';
import { blobToBase64, getBase64 } from 'common/helpers/getBase64.helper';
import FileService from 'common/services/file-service/file.service';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';

class ConfigurationStore {
  configuration: ISystemConfigurationRead | null = null;
  communitySettings: ICommunitySettings | null = null;
  nsSettings: any | null = null;

  constructor() {
    makeAutoObservable(this);
    this.restoreConfiguration();
  }

  getCardClassList(isIncludeDeactivated: boolean) {
    if (isIncludeDeactivated) {
      return this.communitySettings?.cardAccessSettings.cardAccessGroupsSettings?.map((i) => ({ value: i, label: i })) ?? [];
    }
    return (
      this.communitySettings?.cardAccessSettings.cardAccessGroupsSettings
        ?.filter((i) => i !== deactivatedAccessCardName)
        .map((i) => ({ value: i, label: i })) ?? []
    );
  }

  setConfiguration(configuration: ISystemConfigurationRead | null) {
    localStorage.setItem(SYSTEM_CONFIGURATION, JSON.stringify(configuration));
    this.configuration = configuration;
  }

  setCommunitySettings(communitySettings: ICommunitySettings | null) {
    localStorage.setItem(COMMUNITY_SETTINGS, JSON.stringify(communitySettings));
    this.communitySettings = communitySettings;
  }

  setNSSettings(nsSettings: any | null) {
    localStorage.setItem(NS_SETTINGS, JSON.stringify(nsSettings));
    this.nsSettings = nsSettings;
  }

  restoreConfiguration() {
    const configurationSource = localStorage.getItem(SYSTEM_CONFIGURATION);
    this.configuration = configurationSource ? JSON.parse(configurationSource) : null;

    const communitySettings = localStorage.getItem(COMMUNITY_SETTINGS);
    this.communitySettings = communitySettings ? JSON.parse(communitySettings) : null;

    const nsSettings = localStorage.getItem(NS_SETTINGS);
    this.nsSettings = nsSettings ? JSON.parse(nsSettings) : null;
  }

  clearConfiguration() {
    localStorage.removeItem(SYSTEM_CONFIGURATION);
    this.configuration = null;

    localStorage.removeItem(COMMUNITY_SETTINGS);
    this.communitySettings = null;

    localStorage.removeItem(NS_SETTINGS);
    this.communitySettings = null;
  }

  async requestConfiguration() {
    const configuration = await configurationService.getSystemConfiguration();
    this.setConfiguration(configuration);

    const communitySettings = await configurationService.getCommunitySettings();
    this.setCommunitySettings(communitySettings);

    const nsSettings = await configurationService.getNSDisabledFieldsSettings();
    this.setNSSettings(nsSettings);
  }

  get trafficLogDaysToArchive() {
    return this.configuration?.trafficLogArchive?.trafficLogDaysToArchive!;
  }

  get facilityCodes() {
    if (this.configuration?.facilityCodes?.length) {
      return this.configuration?.facilityCodes.map((i) => i.value);
    }
    return [];
  }

  get defaultForVehicle() {
    if (!this.configuration?.facilityCodes?.length) {
      return null;
    }
    const result = this.configuration.facilityCodes.find((i) => i.defaultForType === FacilityCodeType.Vehicle);

    return result?.value ?? this.configuration.facilityCodes[0].value;
  }

  get defaultForAccessCard() {
    if (!this.configuration?.facilityCodes?.length) {
      return null;
    }
    const result = this.configuration.facilityCodes.find((i) => i.defaultForType === FacilityCodeType.AccessCard);

    return result?.value ?? this.configuration.facilityCodes[0].value;
  }

  get currentUserGate() {
    if (userAuthStore.userType !== UserTypeEnum.SecurityOfficer) {
      return null;
    }

    const gateInfo = this.configuration?.gates.find((i) => i.name === userAuthStore.defaultGate);

    return gateInfo;
  }

  get useRtspStream() {
    if (this.currentUserGate?.useRtsp && this.currentUserGate?.rtspUrl) {
      return true;
    }
    return false;
  }

  async getCommunityLogoOrDefaultForPDF() {
    if (this.configuration?.default.communityLogoUrl) {
      try {
        const blob = await FileService.getFile(this.configuration.default.communityLogoUrl, UploadFileTypes.Image);
        return await blobToBase64(blob);
      } catch (e) {
        console.log(e);
        return await getBase64(LogoIcon);
      }
    }

    return await getBase64(LogoIcon);
  }

  async getCommunityLogoForPDF() {
    if (this.configuration?.default.communityLogoUrl) {
      try {
        const blob = await FileService.getFile(this.configuration.default.communityLogoUrl, UploadFileTypes.Image);
        return await blobToBase64(blob);
      } catch (e) {
        console.log(e);
        return null;
      }
    }

    return null;
  }
}

const configurationStore = new ConfigurationStore();
export const ConfigurationStoreContext = React.createContext<ConfigurationStore>(configurationStore);
export default configurationStore;
